'use client';

import { useEffect, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

/**
 * Inner component — must be wrapped in Suspense because useSearchParams
 * opts the component into client-side rendering and requires a boundary.
 *
 * Root cause fix: The isFirstRender guard was skipping the scroll reset on
 * every fresh component mount — which is exactly what happens on each
 * client-side navigation in Next.js App Router (Suspense remounts the child).
 * Removed the guard entirely. The inline <script> in layout.tsx already
 * sets history.scrollRestoration = 'manual' before hydration, so the browser
 * will not restore a saved position on true initial load.
 */
function NavigationWatcher() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // If the navigation target is a hash anchor on the SAME page, let the
    // browser handle it naturally (don't reset scroll).
    if (typeof window !== 'undefined' && window.location?.hash) {
      return;
    }

    const forceScrollTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    // 1. Immediate reset
    forceScrollTop();

    // 2. After first paint — catches layout shifts from React commits
    const raf1 = requestAnimationFrame(() => {
      forceScrollTop();

      // 3. After second paint — catches async/lazy content
      const raf2 = requestAnimationFrame(() => {
        forceScrollTop();
      });

      return () => cancelAnimationFrame(raf2);
    });

    // 4. 100ms fallback — catches IntersectionObserver animations, image loads
    const t1 = setTimeout(forceScrollTop, 100);

    // 5. 300ms final fallback — catches very slow async renders
    const t2 = setTimeout(forceScrollTop, 300);

    return () => {
      cancelAnimationFrame(raf1);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  return null;
}

export default function ScrollToTop() {
  return (
    <Suspense fallback={null}>
      <NavigationWatcher />
    </Suspense>
  );
}
